import { AccountMapping } from './../models/AccountMapping';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule, DatePipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccountMappingService } from '../services/accountMappingService.service';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Status } from '../enums/Status';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { catchError, map, of, startWith, Subject, takeUntil, tap } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { GroupAccountService } from '../services/groupAccountService.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { PagedData } from '../models/PagedData';
import { Page } from '../models/Page';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { GroupAccount, GroupAccountDisplay } from '../models/GroupAccount';
import { RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { UserService } from '../services/user.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
@Component({
  selector: 'app-account-mapping',
  templateUrl: './account-mapping.component.html',
  styleUrl: './account-mapping.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatCheckboxModule,
    FormsModule,
    MatTableModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    RouterModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTooltipModule
  ],
  providers: [DatePipe],
})
export class AccountMappingComponent implements OnInit, AfterViewInit {
  private _liveAnnouncer = inject(LiveAnnouncer);
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  public selectedCompanies: string[] = [];
  public page: Page = {
    pageIndex: 0,
    pageSize: 10,
    length: 0,
    filter: undefined,
    orderBy: 'SourceAccountId',
    orderDirection: 'asc',
    filterKeyword: undefined
  };

  public selectedCompany: string | null = null;
  public companies?: string[] = [];
  public groupAccounts: GroupAccount[] = [];
  public groupAccountsIdAndName: GroupAccountDisplay[] = [];
  public isLoading: boolean = false;
  public downloadingExcel: boolean = false;
  public Status = Status;
  public displayedColumns: string[] = [
    'sourceAccountId',
    'sourceAccountName',
    'sourceDepartmentCode',
    'groupAccountId',
    'groupAccountName',
    'accountType',
    'company',
    'status',
    'created',
    'modified',
    'modifier',
    'locked',
    'actions'
  ];

  public dataSource = new MatTableDataSource<AccountMapping>([]);
  public isSaving = false;
  public status = [
    { id: 0, name: 'OK' },
    { id: 1, name: 'Not mapped' },
    { id: 2, name: 'Awaiting approval' },
  ];
  public filteredGroupAccounts: GroupAccountDisplay[] = [];
  public groupAccountFilterCtrl = new FormControl();
  private readonly _onDestroy = new Subject<void>();
  public sortOrder: 'asc' | 'desc' = 'asc'; // Default to ascending sort
  public currentUserDetails: any;
  public isCurrentUserAdmin: boolean = false;
  public userRole: string | null = null;
  public searchKeyword: string = '';
  public searchingKeywords: boolean = false;
  constructor(
    public dialog: MatDialog,
    private readonly accountMappingService: AccountMappingService,
    private readonly groupAccountService: GroupAccountService,
    private readonly authService: MsalService,
    private readonly userService: UserService,
    public snackBar: MatSnackBar
  ) {}

  async ngOnInit() {
    this.currentUserDetails = this.authService.instance.getActiveAccount();
    this.userService.userRole$.subscribe(role => {
      if (role === 'Admin') {
        this.isCurrentUserAdmin = true;
      }
    });

    this.isLoading = true;
    this.getAllGroupAccounts();
    this.groupAccountFilterCtrl.valueChanges
      .pipe(
        startWith(''),
        map((value) => this._filterGroupAccount(value)),
        takeUntil(this._onDestroy)
      )
      .subscribe((filteredGroupAccounts) => {
        this.filteredGroupAccounts = filteredGroupAccounts;
      });

    this.getCompanies().pipe(
      tap(() => this.loadAccountMappingData(this.page))
    ).subscribe();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  toggleSortOrder() {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.filteredGroupAccounts = this.sortGroupAccounts(
      this.filteredGroupAccounts,
      this.sortOrder
    );
  }

  private sortGroupAccounts(
    ids: GroupAccountDisplay[],
    sortOrder: 'asc' | 'desc'
  ): GroupAccountDisplay[] {
    return ids.sort((a, b) =>
      sortOrder === 'asc' ? a.id - b.id : b.id - a.id
    );
  }

  exportToExcel() {
    this.downloadingExcel = true;

    this.accountMappingService.exportAccountsToExcel(this.page).subscribe({
      next: (blob: Blob) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'Accounts.xlsx';
        link.click();
      },
      error: () => {
        this.snackBar.open('Error exporting data to excel file', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });

        this.downloadingExcel = false;
      },
      complete: () => {
        this.downloadingExcel = false;
      },
    });
  }

  pageChanged(event: PageEvent) {
    this.isLoading = true;
    this.page.pageIndex = event.pageIndex;
    this.page.pageSize = event.pageSize;
    this.loadAccountMappingData(this.page);
  }

  private _filterGroupAccount(
    value: string | number
  ): { id: number; displayName: string }[] {
    if (!value) {
      return this.groupAccountsIdAndName;
    }

    const filterValue = value.toString().toLowerCase();

    return this.groupAccountsIdAndName.filter(
      (groupAccount) =>
        groupAccount.id.toString().startsWith(filterValue) ||
        groupAccount.displayName.toLowerCase().includes(filterValue)
    );
  }

  resetSelection() {
    this.selectedCompanies = [];
    this.page.filter = this.companies;
    this.page.pageIndex = 0;
    this.page.previousPageIndex = 0;
    this.page.length = 0;
    this.loadAccountMappingData(this.page);
  }

  onSearch() {
    this.searchingKeywords = true;
    this.page.filterKeyword = this.searchKeyword;
    this.page.pageIndex = 0;
    this.loadAccountMappingData(this.page);
  }

  clearSearch() {
    this.searchKeyword = '';
    this.page.filterKeyword = '';
    this.loadAccountMappingData(this.page);
  }

  getCompanies() {
    if(this.currentUserDetails?.idTokenClaims) {
      return this.userService.getUsersCompanies(this.currentUserDetails?.idTokenClaims?.oid as string).pipe(
        tap((data: string[]) => {
          this.companies = data;
          this.page.filter = data;
          this.selectedCompanies = data;
        }),
        catchError(() => {
          this.snackBar.open('Error fetching companies', 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          return of(null);
        })
      );
    } else {
      return of(null);
    }
  }

  getAllGroupAccounts() {
    this.groupAccountService.getAllGroupAccounts().subscribe({
      next: (data: GroupAccount[]) => {
        this.groupAccounts = data;
        this.filteredGroupAccounts = this.groupAccountsIdAndName = data.map(
          (account) => ({
            id: account.groupAccountId,
            displayName: `${account.groupAccountName} (${account.groupAccountId})`,
          })
        );

        this.filteredGroupAccounts = this.sortGroupAccounts(
          this.filteredGroupAccounts,
          this.sortOrder
        );
      },
      error: () => {
        this.snackBar.open('Error fetching group account IDs', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  onSelectionChange(event: any) {
    this.page.filter = this.selectedCompanies;
    this.isLoading = true;
    this.page.pageIndex = 0;
    this.page.previousPageIndex = 0;
    this.page.length = 0;
    this.loadAccountMappingData(this.page);
  }

  loadAccountMappingData(page: Page) {
    if (!this.searchingKeywords) {
      this.isLoading = true;
    }

    this.page.pageIndex = page.pageIndex;

    this.accountMappingService.getAccountMappingData(page).subscribe({
      next: (data: PagedData<any>) => {
        this.searchingKeywords = false;
        this.isLoading = false;
        this.dataSource.data = data.data;
        this.page.length = data.page.length;
        this.page = data.page;
        this.dataSource.data = data.data.map((element) => ({
          ...element,
          savedGroupAccountId: element.groupAccountId,
        }));
      },
      error: () => {
        this.isLoading = false;
        this.searchingKeywords = false;
        this.snackBar.open('Error fetching account mapping data', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  discardChanges(element: AccountMapping): void {
    element.groupAccountId = element.originalGroupAccountId ?? null;
    element.groupAccountName = element.originalGroupAccountName;
    element.accountType = element.originalAccountType;
    element.status = element.originalStatus;
    element.locked = element.originalLocked;
    element.modified = element.originalModified;
    element.modifier = element.originalModifier;
    element.modifiedString = element.originalModifiedString;
    element.isEditing = false;
    element.isSaving = false;
  }

  editElement(element: AccountMapping): void {
    element.originalGroupAccountId = element.groupAccountId ?? null;
    element.originalGroupAccountName = element.groupAccountName;
    element.originalAccountType = element.accountType;
    element.originalStatus = element.status;
    element.originalLocked = element.locked;
    element.originalModified = element.modified;
    element.originalModifier = element.modifier;
    element.originalModifiedString = element.modifiedString;
    element.isEditing = true;
  }

  saveElement(element: AccountMapping): void {
    let isOnlyLockChanged = false;
    element.isSaving = true;
    element.modified = new Date();
    element.modifiedString = new Date().toISOString();
    element.modifier = this.currentUserDetails.name;
    debugger;
    if (element.locked != element.originalLocked && element.originalGroupAccountId === element.groupAccountId) {
      isOnlyLockChanged = true;
    }

    this.updateItem(element, true, isOnlyLockChanged);
  }

  updateItem(element: AccountMapping, isEdit: boolean, isOnlyLockChanged: boolean) {
    this.accountMappingService.updateItem(element, isEdit, isOnlyLockChanged).subscribe({
      next: () => {
        element.isEditing = false;
        element.isSaving = false;
        element.approving = false;
        element.savedGroupAccountId = element.groupAccountId;
        if (isEdit && !isOnlyLockChanged) {
          element.status = Status.AwaitingApproval;
        } else {
          element.status = Status.OK;
        }

        this.snackBar.open('Update successful', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
      error: () => {
        this.snackBar.open('Update failed. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });

        this.discardChanges(element);
      },
    });
  }

  approveElement(element: AccountMapping) {
    element.approving = true;
    this.updateItem(element, false, false);
  }

  getGroupAccountById(id: number): GroupAccount | undefined {
    return this.groupAccounts.find((account) => account.groupAccountId === id);
  }

  getGroupAccountNameAndType(element: AccountMapping) {
    element.isLoadingGroupAccountName = true;
    element.isLoadingGroupType = true;

    if (element.groupAccountId !== null) {
      const foundAccount = this.getGroupAccountById(element.groupAccountId);

      if (foundAccount) {
        element.groupAccountName = foundAccount.groupAccountName;
        element.isLoadingGroupAccountName = false;
        element.accountType = foundAccount.accountType;
        element.isLoadingGroupType = false;
      } else {
        element.isLoadingGroupAccountName = false;
        element.isLoadingGroupType = false;
        this.snackBar.open('Group account not found', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      }
    }
  }

  announceSortChange(sortState: Sort) {
    this.isLoading = true;
    this.page.pageIndex = 0;
    this.page.previousPageIndex = 0;
    this.page.length = 0;
    this.page.orderBy = sortState.active;
    this.page.orderDirection = sortState.direction;
    this.loadAccountMappingData(this.page);
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }
}
